import { AnchorButton, FormGroup, PopoverInteractionKind, Position, Spinner, Tooltip } from '@blueprintjs/core'
import * as React from 'react'
import Client from '../../../../../client'
import { LoadableSelect } from '../../../../../components/Inputs'
import config from '../../../../../config'
import useAPI from '../../../../../hooks/useAPI'
import { UprightStyle } from '../../../../../types'
import { renderError, stylizeTitle } from '../Helpers'
import { GutterTooltipError } from '../GutterTooltipError'
import { debounce } from '../../../../../utils'
import { safeIsEmpty } from '../../../../express_list/utils'

interface ShippingBoxSelectProps {
  error?: string
  onChange: ({ name, value }: { name: string; value: any }) => void
  value: number
  length: string // these are strings b/c we use it with controlled components, which would trim off decimals as you type them in if this was a numeric type
  width: string // ^
  height: string // ^
  padding: number // this can be a number b/c it comes from a select component, which doesn't have this ^ issue
  autoload: boolean
  errorTooltipOffset?: number
  style?: React.CSSProperties
  labelInfo?: React.ReactNode
  autoCalculate?: boolean
}

const styles: UprightStyle = {
  tooltip: { maxWidth: 300 }
}

// Wraps blueprint FormGroup, to provide a topRightWidget prop for a component in the top-right.
const FormGroupTopRightWidget = ({ label, topRightWidget, style, ...props }) => {
  const labelWithWidget = (
    <div>
      {label}
      <div style={{ float: 'right', position: 'relative', bottom: '3px' }}>{topRightWidget}</div>
    </div>
  )

  return (
    <div style={style}>
      <FormGroup className="nuke-top-margin-loadable-select" {...props} label={labelWithWidget}>
        <div className="shipping-box-select-top-right" style={{ marginTop: '-6px' }}>
          {props.children}
        </div>
      </FormGroup>
    </div>
  )
}

export function validateDimensions(length: string, width: string, height: string): boolean {
  return parseFloat(length) > 0 && parseFloat(width) > 0 && parseFloat(height) > 0
}

const calculateBoxSizeRequest = (length, width, height, neverUndefinedPadding) => {
  return Client.calculateBestShippingBox(
    parseFloat(length),
    parseFloat(width),
    parseFloat(height),
    neverUndefinedPadding
  )
}

export const ShippingBoxSelect = React.memo(
  ({
    error,
    onChange,
    value,
    length,
    width,
    height,
    padding,
    autoload,
    errorTooltipOffset,
    style,
    labelInfo,
    autoCalculate
  }: ShippingBoxSelectProps) => {
    // We always want to pass this param to the endpoint, to avoid using legacy shipping_box_padding_inches field.
    // Zero most closely represents "no padding selected" for the purposes of calculation.
    // Specifically needed when loading the new product form and hitting "calculate" without touching Box Padding.
    const neverUndefinedPadding = padding || 0

    const [{ loading: boxCalcLoading, data: boxCalcData, error: boxCalcError }, boxCalcRefresh] =
      useAPI(calculateBoxSizeRequest)

    React.useEffect(() => {
      if (boxCalcData) {
        if (boxCalcData.bestBox) {
          onChange({ name: 'shippingBoxId', value: boxCalcData.bestBox.id })
        } else {
          // If they already chose a shipping box, then they hit Calculate and no box was found, clear out the chosen shipping box.
          // Otherwise they might not notice that the calculation was fruitless,
          // and might unknowingly proceed with the previously selected box (which isn't what they want).
          onChange({ name: 'shippingBoxId', value: null })
        }
      }
    }, [boxCalcData, onChange])

    // "debounce" confuses the linter. We are upholding the lint rule philosophy.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedBoxCalcRefresh = React.useCallback(debounce(boxCalcRefresh, 500), [boxCalcRefresh])

    React.useEffect(() => {
      if (autoCalculate && validateDimensions(length, width, height)) {
        debouncedBoxCalcRefresh(length, width, height, neverUndefinedPadding)
      }
    }, [length, width, height, neverUndefinedPadding, autoCalculate, debouncedBoxCalcRefresh])

    const calculateButtonEnabled = validateDimensions(length, width, height)

    const tooltipText = React.useMemo(() => {
      if (!calculateButtonEnabled) return 'Enter length, width, and height first'

      return 'Oddly shaped items may require custom boxes to reduce shipping cost'
    }, [calculateButtonEnabled])

    let topRightWidget
    if (autoCalculate) {
      topRightWidget = boxCalcLoading && <Spinner size={18} />
    } else {
      topRightWidget = (
        <Tooltip
          interactionKind={PopoverInteractionKind.HOVER}
          position={Position.TOP_RIGHT}
          content={<div style={styles.tooltip}>{tooltipText}</div>}
        >
          <AnchorButton
            minimal
            text="Calculate"
            intent="primary"
            small
            disabled={!calculateButtonEnabled}
            onClick={() => {
              boxCalcRefresh(length, width, height, neverUndefinedPadding)
            }}
            loading={boxCalcLoading}
          />
        </Tooltip>
      )
    }

    let helperText

    if (autoCalculate) {
      if (safeIsEmpty(value?.toString())) {
        helperText = 'Enter item dimensions to auto-fill box'

        if (config.PACKSIZE_ORG()) {
          helperText = 'Enter item dimensions to auto-fill box. Leave blank for Packsize.'
        }
      } else if (value === boxCalcData?.bestBox?.id && !boxCalcLoading) {
        // Show this as long as the Shipping Box dropdown still has the calculation result
        helperText = `${boxCalcData.params.length + boxCalcData.params.padding} x ${
          parseFloat(boxCalcData.params.width) + boxCalcData.params.padding
        } x ${parseFloat(boxCalcData.params.height) + boxCalcData.params.padding} used to auto-fill box size`
      }
    }

    if (!boxCalcLoading) {
      if (config.PACKSIZE_ORG() && !autoCalculate) {
        helperText = <span>Leave blank for Packsize.</span>
      }

      if (error) {
        // if the Product endpoint responded with a failure for this field
        helperText = renderError(error)
      } else if (boxCalcError) {
        // if there was an error when calculating the best shipping box
        helperText = renderError('Something went wrong, please try again')
      } else if (boxCalcData && !boxCalcData.bestBox) {
        helperText = <span>None of your shipping boxes will fit this product.</span>
      }
    }

    return (
      <FormGroupTopRightWidget
        topRightWidget={topRightWidget}
        label={stylizeTitle('Shipping Box', error)}
        helperText={helperText}
        intent={error ? 'danger' : 'none'}
        style={style}
        labelInfo={labelInfo}
      >
        <GutterTooltipError isOpen={!!error} offsetPx={errorTooltipOffset} position={Position.LEFT}>
          <LoadableSelect
            load={Client.shippingBoxIndex}
            process={d =>
              d.shippingBoxes.map(s => ({
                label: s.name,
                value: s.id
              }))
            }
            onChange={v => onChange({ name: 'shippingBoxId', value: v })}
            value={value}
            autoload={autoload}
            stableOptionOrder
          />
        </GutterTooltipError>
      </FormGroupTopRightWidget>
    )
  }
)
